import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { treatmentPlanV3Actions } from '../../../store';
import { hasFamilyFieldErrors } from '../../common';
import { hasAssessmentFormErrors } from '../components/forms/assessment';
import { hasChildFormErrors } from '../components/forms/child';
import { hasDischargeFormErrors } from '../components/forms/discharge';
import { hasEducationFormErrors } from '../components/forms/education';
import { hasMedicationFormErrors } from '../components/forms/medication';
import { hasReferralFormErrors } from '../components/forms/referral';
import { hasTherapyFormErrors } from '../components/forms/therapy';
import { hasTreatmentFormErrors } from '../components/forms/treatment';
import useTreatmentPlanV3PlanBay from './useTreatmentPlanV3PlanBay';
import useTreatmentPlanV3TabPages from './useTreatmentPlanV3TabPages';
import { getCrisisTabDecoration, getTabPageDecoration } from './utils';

import type { AppDispatch } from '../../../store';
import type { GetAccessTokenSilently } from '../../../utils/types';

function loadTreatmentPlanV3TabPages(
  dispatch: AppDispatch,
  treatmentPlanId: string | undefined,
  getAccessTokenSilently: GetAccessTokenSilently,
) {
  if (!treatmentPlanId) return;

  const payload = { treatmentPlanId, getAccessTokenSilently };
  dispatch(treatmentPlanV3Actions.getTreatmentPlanV3AssessmentPage(payload));
  dispatch(treatmentPlanV3Actions.getTreatmentPlanV3ChildPage(payload));
  dispatch(treatmentPlanV3Actions.getTreatmentPlanV3CrisisPage(payload));
  dispatch(treatmentPlanV3Actions.getTreatmentPlanV3DischargePage(payload));
  dispatch(treatmentPlanV3Actions.getTreatmentPlanV3EducationPage(payload));
  dispatch(treatmentPlanV3Actions.getTreatmentPlanV3FamilyPage(payload));
  dispatch(treatmentPlanV3Actions.getTreatmentPlanV3MedicationPage(payload));
  dispatch(treatmentPlanV3Actions.getTreatmentPlanV3ReferralPage(payload));
  dispatch(treatmentPlanV3Actions.getTreatmentPlanV3TherapyPage(payload));
  dispatch(treatmentPlanV3Actions.getTreatmentPlanV3TreatmentPage(payload));
}

export default function useTreatmentPlanV3TabStatus() {
  const dispatch = useDispatch<AppDispatch>();
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const { getAccessTokenSilently } = useAuth0();
  const pages = useTreatmentPlanV3TabPages();
  const planBay = useTreatmentPlanV3PlanBay();

  useEffect(() => {
    loadTreatmentPlanV3TabPages(dispatch, treatmentPlanId, getAccessTokenSilently);
  }, [dispatch, getAccessTokenSilently, treatmentPlanId]);

  const assessment = useMemo(
    () => getTabPageDecoration(pages.assessment, treatmentPlanId, hasAssessmentFormErrors),
    [pages.assessment, treatmentPlanId],
  );
  const child = useMemo(
    () => getTabPageDecoration(pages.child, treatmentPlanId, hasChildFormErrors),
    [pages.child, treatmentPlanId],
  );
  const crisis = useMemo(
    () => getCrisisTabDecoration(pages.crisis, pages.assessment, treatmentPlanId),
    [pages.assessment, pages.crisis, treatmentPlanId],
  );
  const discharge = useMemo(
    () => getTabPageDecoration(pages.discharge, treatmentPlanId, hasDischargeFormErrors),
    [pages.discharge, treatmentPlanId],
  );
  const education = useMemo(
    () => getTabPageDecoration(pages.education, treatmentPlanId, hasEducationFormErrors),
    [pages.education, treatmentPlanId],
  );
  const family = useMemo(
    () => getTabPageDecoration(pages.family, treatmentPlanId, hasFamilyFieldErrors),
    [pages.family, treatmentPlanId],
  );
  const medication = useMemo(
    () => getTabPageDecoration(pages.medication, treatmentPlanId, hasMedicationFormErrors),
    [pages.medication, treatmentPlanId],
  );
  const referral = useMemo(
    () => getTabPageDecoration(pages.referral, treatmentPlanId, hasReferralFormErrors),
    [pages.referral, treatmentPlanId],
  );
  const therapy = useMemo(
    () => getTabPageDecoration(pages.therapy, treatmentPlanId, hasTherapyFormErrors),
    [pages.therapy, treatmentPlanId],
  );
  const treatment = useMemo(
    () =>
      getTabPageDecoration(pages.treatment, treatmentPlanId, (page) =>
        hasTreatmentFormErrors(page, planBay),
      ),
    [pages.treatment, planBay, treatmentPlanId],
  );

  return {
    assessment,
    child,
    crisis,
    discharge,
    education,
    family,
    medication,
    referral,
    therapy,
    treatment,
  };
}
